// Each row describes its own observation. Different rows are not combined
// into a stock-price cause, and cumulative flow is not a trend reversal.
type Tone = 'up' | 'down' | 'neutral' | 'warn';
type RowKind =
    'price'
    | 'turnover'
    | 'net-flow'
    | 'flow-trend'
    | 'sector'
    | 'derivative'
    | 'fx'
    | 'global';

export type WhyMovingRowInput = {
  key: string;
  label: string;
  kind: RowKind;
  observedAt: string;
  stale?: boolean;
  matched?: boolean;
  changeRate?: number | null;
  amount?: number | null;
  marketBasis?: number | null;
  oiChangeCumulative?: number | null;
  directionState?: string;
  directionDataStatus?: string;
  trendPointCount?: number;
  turnedAt?: string;
};

export type WhyMovingRow = {
  key: string;
  label: string;
  summary: string;
  timeLabel: string;
  timeDetail: string;
  sourceDetail: string;
  tone: Tone;
};

const KST_OFFSET = 9 * 60 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

export function whyObservationEpoch(value: string): number {
  const raw = value.trim().replace(' ', 'T');
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return NaN;
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}+09:00`);
}

function sessionDay(epoch: number): string {
  return new Date(epoch + KST_OFFSET).toISOString().slice(0, 10);
}

function finite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function directionTone(value: number): Tone {
  return value > 0 ? 'up' : value < 0 ? 'down' : 'neutral';
}

function absoluteMoney(value: number): string {
  const amount = Math.abs(value);
  const [unit, divisor] = amount >= 1e12 ? ['조', 1e12] as const
      : amount >= 1e8 ? ['억', 1e8] as const
          : amount >= 1e4 ? ['만', 1e4] as const : ['원', 1] as const;
  return `${(amount / divisor).toLocaleString('ko-KR', {maximumFractionDigits: 1})}${unit}`;
}

function priceMove(value: number | null | undefined): string {
  if (!finite(value)) return '등락 확인 중';
  if (value === 0) return '보합';
  // These are daily returns, not evidence of a short-term reversal.
  return `${Math.abs(value).toFixed(2)}% ${value > 0 ? '상승' : '하락'}`;
}

export function summarizeWhyMovingRow(
    input: WhyMovingRowInput,
    {marketOpen, now = Date.now()}: { marketOpen: boolean; now?: number },
): WhyMovingRow {
  const observed = whyObservationEpoch(input.observedAt);
  const validTime = Number.isFinite(observed) && observed <= now + 60_000;
  const delayed = Boolean(input.stale) || (validTime && marketOpen && now - observed > FIVE_MINUTES);
  const dated = validTime
      ? new Date(observed + KST_OFFSET).toISOString().slice(0, 16).replace('T', ' ')
      : '';
  // Always preserve each row's own date; a missing price timestamp must not
  // hide the independent futures, FX or flow observations.
  const row: WhyMovingRow = {
    key: input.key, label: input.label, summary: '', tone: 'neutral',
    timeLabel: dated ? `${dated.slice(5).replace('-', '.')} 기준${delayed && marketOpen ? ' · 지연' : ''}` : '시각 미확인',
    timeDetail: dated ? `${dated} KST 관측 기준${delayed ? ' · 갱신 확인 필요' : ''}` : '원천 관측 시각 확인 필요',
    sourceDetail: '',
  };
  const usableDirection = validTime && !delayed;

  switch (input.kind) {
    case 'price':
    case 'sector':
    case 'global':
      row.summary = priceMove(input.changeRate);
      if (finite(input.changeRate)) row.tone = directionTone(input.changeRate);
      break;
    case 'fx':
      row.summary = !finite(input.changeRate) ? '환율 확인 중'
          : input.changeRate === 0 ? '환율 보합'
              : `${Math.abs(input.changeRate).toFixed(2)}% ${input.changeRate < 0 ? '하락 · 원화 강세' : '상승 · 원화 약세'}`;
      // FX direction alone is not a favorable/unfavorable stock signal.
      break;
    case 'turnover':
      row.summary = finite(input.amount) && input.amount >= 0 ? `당일 누적 ${absoluteMoney(input.amount)}` : '거래대금 확인 중';
      break;
    case 'net-flow':
      if (input.matched === false || !finite(input.amount)) {
        row.summary = '해당 거래일 수급 미확인';
      } else if (input.amount === 0) {
        row.summary = '누적 순매수 0원';
      } else {
        row.summary = `${absoluteMoney(input.amount)} 누적 ${input.amount > 0 ? '순매수' : '순매도'}`;
        row.tone = directionTone(input.amount);
      }
      break;
    case 'flow-trend': {
      const up = ['UP_STREAK', 'DOWN_TO_UP'].includes(input.directionState || '');
      const down = ['DOWN_STREAK', 'UP_TO_DOWN'].includes(input.directionState || '');
      if (!usableDirection || (input.trendPointCount ?? 0) < 2 || (!up && !down)) {
        row.summary = '수급 변화 확인 중';
        break;
      }
      const turned = whyObservationEpoch(input.turnedAt || '');
      const turnedOnThisDay = Number.isFinite(turned) && turned <= observed && sessionDay(turned) === sessionDay(observed);
      const turn = ['DOWN_TO_UP', 'UP_TO_DOWN'].includes(input.directionState || '') && turnedOnThisDay;
      row.summary = turn ? `수급이 ${up ? '개선으로' : '약화로'} 전환` : `수급 ${up ? '개선' : '약화'} 흐름`;
      row.tone = up ? 'up' : 'down';
      break;
    }
    case 'derivative': {
      const rate = input.changeRate;
      const states: Record<string, [string, Tone]> = {
        UP: ['단기 상승 우세', 'up'], DOWN: ['단기 하락 우세', 'down'],
        UP_REVERSAL_WATCH: ['반등 신호 관찰', 'up'],
        DOWN_REVERSAL_WATCH: ['하락 전환 신호 관찰', 'down'],
        MIXED: ['단기 방향 혼조', 'neutral'],
      };
      const state = usableDirection && input.directionDataStatus === 'GOOD'
          ? states[input.directionState || ''] : undefined;
      // A short-term state and a daily return may legitimately disagree.
      const dailyDirection = !finite(rate) ? '등락 미확인' : rate > 0 ? '상승' : rate < 0 ? '하락' : '보합';
      const opposed = state && finite(rate)
          && ((rate > 0 && state[1] === 'down') || (rate < 0 && state[1] === 'up'));
      const movement = state
          ? `${opposed ? `전일 ${dailyDirection} · ` : ''}${state[0]}`
          : dailyDirection;
      const basis = !finite(input.marketBasis) ? '현물 가격차 미확인'
          : input.marketBasis > 0 ? '현물보다 높음' : input.marketBasis < 0 ? '현물보다 낮음' : '현물과 같은 가격';
      const oi = !finite(input.oiChangeCumulative) ? '미결제 미확인'
          : input.oiChangeCumulative > 0 ? '미결제 증가' : input.oiChangeCumulative < 0 ? '미결제 감소' : '미결제 변화 없음';
      // OI growth does not identify buy-side positioning; basis sign alone
      // does not establish arbitrage or program buying/selling.
      row.summary = `${movement} · ${basis} · ${oi}`;
      row.sourceDetail = [
        finite(rate) ? `전일 대비 ${rate > 0 ? '+' : ''}${rate.toFixed(2)}%` : '등락률 미확인',
        finite(input.marketBasis) ? `베이시스 ${input.marketBasis.toFixed(2)}` : '베이시스 미확인',
        finite(input.oiChangeCumulative) ? `미결제약정 누적 증감 ${input.oiChangeCumulative.toLocaleString('ko-KR')}` : '미결제약정 증감 미확인',
      ].join(' · ');
      row.tone = state?.[1] ?? (finite(rate) ? directionTone(rate) : 'neutral');
      break;
    }
  }
  if (!validTime || delayed) row.tone = 'neutral';
  return row;
}
